import type { Vehicle } from '@/types';

const STATUS_COLORS: Record<
  string,
  {
    fill: string;
    glow: string;
  }
> = {
  moving: {
    fill: '#22c55e',
    glow: 'rgba(34, 197, 94, 0.42)',
  },
  idle: {
    fill: '#f59e0b',
    glow: 'rgba(245, 158, 11, 0.42)',
  },
  stopped: {
    fill: '#64748b',
    glow: 'rgba(100, 116, 139, 0.34)',
  },
  offline: {
    fill: '#94a3b8',
    glow: 'rgba(148, 163, 184, 0.32)',
  },
  alert: {
    fill: '#ef4444',
    glow: 'rgba(239, 68, 68, 0.5)',
  },
  maintenance: {
    fill: '#8b5cf6',
    glow: 'rgba(139, 92, 246, 0.38)',
  },
};

const LOCATION_POINT_COLOR = '#ef4444';

const DEFAULT_VEHICLE_COLORS = [
  '#ef4444',
  '#2563eb',
  '#16a34a',
  '#f97316',
  '#db2777',
  '#0891b2',
  '#84cc16',
  '#7c3aed',
  '#ea580c',
  '#0d9488',
];

const VEHICLE_TYPE_MAP: [RegExp, string][] = [
  [/truck|lorry|卡车/, 'truck'],
  [/bus|巴士|公交/, 'bus'],
  [/van|厢式/, 'van'],
  [/taxi|出租/, 'taxi'],
  [/motorcycle|motorbike|摩托/, 'motocycle'],
  [/bicycle|bike|自行/, 'bicycle'],
  [/scooter/, 'scooter'],
  [/plane|airplane|飞机/, 'plane'],
  [/helicopter|直升/, 'helicopter'],
  [/ship|轮船/, 'ship'],
  [/boat|船/, 'boat'],
  [/train|火车/, 'train'],
  [/tram|电车/, 'tram'],
  [/pickup/, 'pickup'],
  [/trailer|拖车/, 'trailer'],
  [/tractor|拖拉机/, 'tractor'],
  [/crane|吊车/, 'crane'],
  [/camper|rv|房车/, 'camper'],
  [/person|pedestrian|行人/, 'person'],
  [/animal|cattle|牲畜/, 'animal'],
];

const VALID_VEHICLE_TYPES = [
  'car',
  'truck',
  'bus',
  'van',
  'taxi',
  'motocycle',
  'bicycle',
  'scooter',
  'plane',
  'helicopter',
  'ship',
  'boat',
  'train',
  'tram',
  'pickup',
  'trailer',
  'tractor',
  'crane',
  'camper',
  'person',
  'animal',
];

function resolveStatus(vehicle: Vehicle): string {
  return vehicle?.status &&
    STATUS_COLORS[vehicle.status]
    ? vehicle.status
    : 'stopped';
}

function resolveVehicleType(
  vehicle: Vehicle,
): string {
  const type = (
    vehicle?.iconType || ''
  )
    .toLowerCase()
    .trim();

  if (
    type &&
    VALID_VEHICLE_TYPES.includes(type)
  ) {
    return type;
  }

  const model = (
    vehicle?.model || ''
  ).toLowerCase();

  for (
    const [pattern, vehicleType]
    of VEHICLE_TYPE_MAP
  ) {
    if (pattern.test(model)) {
      return vehicleType;
    }
  }

  return 'car';
}

function normalizeColor(
  value: string | undefined,
): string | null {
  if (!value) {
    return null;
  }

  const color = value.trim();

  if (
    /^#[0-9a-fA-F]{6}$/.test(color)
  ) {
    return color.toLowerCase();
  }

  if (
    /^#[0-9a-fA-F]{3}$/.test(color)
  ) {
    const [r, g, b] =
      color.slice(1);

    return (
      `#${r}${r}${g}${g}${b}${b}`
    ).toLowerCase();
  }

  return null;
}

function resolveVehicleColor(
  vehicle: Vehicle,
): string {
  const customColor =
    normalizeColor(
      vehicle.iconColor,
    );

  if (customColor) {
    return customColor;
  }

  const numericId =
    Number(vehicle.id);

  const index =
    Number.isFinite(numericId)
      ? Math.abs(
          Math.trunc(numericId),
        ) %
        DEFAULT_VEHICLE_COLORS.length
      : 0;

  return (
    DEFAULT_VEHICLE_COLORS[index] ??
    '#f97316'
  );
}

function escapeHtml(
  value:
    | string
    | number
    | null
    | undefined,
): string {
  if (value == null) {
    return '';
  }

  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function plateText(
  vehicle: Vehicle,
): string {
  return (
    vehicle?.name?.trim() ||
    vehicle?.plate ||
    ''
  );
}

function getMarkerIconSrc(
  vehicle: Vehicle,
): string {
  const type =
    resolveVehicleType(vehicle);

  /*
   * La forma del vehículo ya no depende
   * del estado. El estado se muestra
   * mediante el pequeño indicador.
   */
  return `/markers/moving_${type}.svg`;
}

function bubbleShadow(
  selected: boolean,
): string {
  return selected
    ? '0 0 0 3px rgba(37,99,235,0.90), 0 4px 14px rgba(0,0,0,0.32)'
    : '0 3px 12px rgba(0,0,0,0.30)';
}

function markerHtml(
  vehicle: Vehicle,
  status: string,
  isSelected: boolean,
  rotation: number,
): string {
  const statusColor =
    STATUS_COLORS[status] ??
    STATUS_COLORS.stopped;

  const iconColor =
    resolveVehicleColor(vehicle);

  const iconSrc =
    getMarkerIconSrc(vehicle);

  const label =
    escapeHtml(
      plateText(vehicle),
    );

  return `
    <div
      class="fleet-car-visual"
      style="
        position:absolute;
        left:0;
        top:0;
        width:0;
        height:0;
        pointer-events:none;
        overflow:visible;
      "
    >
      <!-- BURBUJA DEL VEHÍCULO -->
      <div
        class="fleet-car-bubble"
        style="
          position:absolute;
          left:0;
          bottom:34px;
          width:58px;
          height:58px;
          transform:translateX(-50%);
          box-sizing:border-box;
          display:flex;
          align-items:center;
          justify-content:center;
          border-radius:50%;
          background:#ffffff;
          border:2px solid rgba(255,255,255,0.98);
          box-shadow:${bubbleShadow(isSelected)};
          pointer-events:auto;
          z-index:4;
        "
      >
        <!-- INDICADOR PEQUEÑO DE ESTADO -->
        <span
          class="fleet-status-dot"
          style="
            position:absolute;
            left:-1px;
            top:-1px;
            width:12px;
            height:12px;
            box-sizing:border-box;
            border-radius:50%;
            background:${statusColor.fill};
            border:2px solid #ffffff;
            box-shadow:
              0 1px 4px rgba(0,0,0,0.30);
            z-index:8;
          "
        ></span>

        <!-- VEHÍCULO -->
        <div
          class="fleet-car-rotator"
          style="
            position:relative;
            width:48px;
            height:48px;
            display:flex;
            align-items:center;
            justify-content:center;
            transform:rotate(${rotation}deg);
            transform-origin:center center;
            transition:transform 120ms linear;
          "
        >
          <div
            class="fleet-car-art"
            style="
              position:relative;
              width:46px;
              height:46px;
            "
          >
            <img
              src="${iconSrc}"
              width="46"
              height="46"
              alt=""
              draggable="false"
              style="
                position:absolute;
                inset:0;
                width:46px;
                height:46px;
                object-fit:contain;
                display:block;
                user-select:none;
                -webkit-user-drag:none;
                filter:
                  grayscale(1)
                  contrast(1.08)
                  brightness(1.08);
              "
            />

            <span
              class="fleet-car-color-layer"
              style="
                position:absolute;
                inset:0;
                width:46px;
                height:46px;
                background:${iconColor};
                -webkit-mask-image:url('${iconSrc}');
                mask-image:url('${iconSrc}');
                -webkit-mask-repeat:no-repeat;
                mask-repeat:no-repeat;
                -webkit-mask-position:center;
                mask-position:center;
                -webkit-mask-size:contain;
                mask-size:contain;
                mix-blend-mode:color;
                opacity:0.96;
                pointer-events:none;
              "
            ></span>
          </div>
        </div>
      </div>

      <!-- NOMBRE DEL VEHÍCULO -->
      ${
        label
          ? `
            <span
              class="fleet-car-label"
              style="
                position:absolute;
                left:0;
                bottom:17px;
                transform:translateX(-50%);
                z-index:5;
                max-width:105px;
                overflow:hidden;
                text-overflow:ellipsis;
                font-size:9px;
                font-weight:700;
                line-height:1.2;
                color:#111827;
                background:rgba(255,255,255,0.97);
                border:1px solid rgba(15,23,42,0.14);
                border-radius:3px;
                padding:1px 4px;
                white-space:nowrap;
                font-family:
                  Inter,
                  system-ui,
                  sans-serif;
                box-shadow:
                  0 1px 3px rgba(0,0,0,0.18);
                pointer-events:auto;
              "
            >
              ${label}
            </span>
          `
          : ''
      }

      <!-- LÍNEA ENTRE LA BURBUJA Y LA POSICIÓN -->
      <div
        class="fleet-location-stem"
        style="
          position:absolute;
          left:-1px;
          bottom:5px;
          width:2px;
          height:13px;
          background:rgba(100,116,139,0.65);
          box-shadow:
            0 0 0 1px rgba(255,255,255,0.75);
          pointer-events:none;
          z-index:1;
        "
      ></div>

      <!--
        PUNTO GPS.
        Su centro coincide exactamente
        con la coordenada del dispositivo.
      -->
      <div
        class="fleet-location-point"
        style="
          position:absolute;
          left:0;
          top:0;
          width:15px;
          height:15px;
          transform:translate(-50%,-50%);
          box-sizing:border-box;
          border-radius:50%;
          background:${LOCATION_POINT_COLOR};
          border:3px solid #ffffff;
          box-shadow:
            0 1px 5px rgba(0,0,0,0.42);
          pointer-events:none;
          z-index:6;
        "
      ></div>
    </div>
  `;
}

export function createCarMarkerElement(
  vehicle: Vehicle,
  isSelected: boolean,
): HTMLDivElement {
  const status =
    resolveStatus(vehicle);

  const rotation =
    Number.isFinite(
      Number(vehicle?.course),
    )
      ? Number(vehicle.course)
      : 0;

  const type =
    resolveVehicleType(vehicle);

  const color =
    resolveVehicleColor(vehicle);

  const label =
    plateText(vehicle);

  const root =
    document.createElement('div');

  /*
   * El elemento raíz mide 0x0.
   * MapLibre coloca su centro exactamente
   * en la coordenada GPS.
   *
   * Todo el marcador se dibuja alrededor
   * de ese punto.
   */
  root.style.cssText =
    'width:0;height:0;cursor:pointer;z-index:1;overflow:visible;will-change:transform;';

  root.setAttribute(
    'data-vehicle-id',
    String(vehicle.id),
  );

  root.setAttribute(
    'data-course',
    String(rotation),
  );

  root.setAttribute(
    'data-status',
    status,
  );

  root.setAttribute(
    'data-selected',
    String(isSelected),
  );

  root.setAttribute(
    'data-icon-type',
    type,
  );

  root.setAttribute(
    'data-icon-color',
    color,
  );

  root.setAttribute(
    'data-label',
    label,
  );

  root.style.zIndex =
    isSelected
      ? '10'
      : '1';

  const inner =
    document.createElement('div');

  inner.className =
    'fleet-car-marker';

  inner.style.cssText =
    'position:absolute;left:0;top:0;width:0;height:0;overflow:visible;';

  inner.innerHTML =
    markerHtml(
      vehicle,
      status,
      isSelected,
      rotation,
    );

  root.appendChild(inner);

  root.addEventListener(
    'click',
    (event) =>
      event.stopPropagation(),
  );

  return root;
}

export function updateCarMarkerElement(
  el: HTMLDivElement,
  vehicle: Vehicle,
  isSelected: boolean,
): void {
  const inner =
    el.querySelector(
      '.fleet-car-marker',
    ) as HTMLElement | null;

  if (!inner) {
    return;
  }

  const status =
    resolveStatus(vehicle);

  const rotation =
    Number.isFinite(
      Number(vehicle?.course),
    )
      ? Number(vehicle.course)
      : 0;

  const type =
    resolveVehicleType(vehicle);

  const color =
    resolveVehicleColor(vehicle);

  const label =
    plateText(vehicle);

  const selectedValue =
    String(isSelected);

  const courseChanged =
    el.getAttribute(
      'data-course',
    ) !== String(rotation);

  const statusChanged =
    el.getAttribute(
      'data-status',
    ) !== status;

  const selectedChanged =
    el.getAttribute(
      'data-selected',
    ) !== selectedValue;

  const typeChanged =
    el.getAttribute(
      'data-icon-type',
    ) !== type;

  const colorChanged =
    el.getAttribute(
      'data-icon-color',
    ) !== color;

  const labelChanged =
    el.getAttribute(
      'data-label',
    ) !== label;

  el.setAttribute(
    'data-course',
    String(rotation),
  );

  el.setAttribute(
    'data-status',
    status,
  );

  el.setAttribute(
    'data-selected',
    selectedValue,
  );

  el.setAttribute(
    'data-icon-type',
    type,
  );

  el.setAttribute(
    'data-icon-color',
    color,
  );

  el.setAttribute(
    'data-label',
    label,
  );

  if (selectedChanged) {
    el.style.zIndex =
      isSelected
        ? '10'
        : '1';
  }

  /*
   * Si solamente cambia el rumbo,
   * rotamos únicamente el vehículo.
   *
   * La burbuja, etiqueta y punto GPS
   * permanecen siempre derechos.
   */
  if (
    courseChanged &&
    !statusChanged &&
    !selectedChanged &&
    !typeChanged &&
    !colorChanged &&
    !labelChanged
  ) {
    const rotator =
      inner.querySelector(
        '.fleet-car-rotator',
      ) as HTMLElement | null;

    if (rotator) {
      rotator.style.transform =
        `rotate(${rotation}deg)`;
    }

    return;
  }

  if (
    courseChanged ||
    statusChanged ||
    selectedChanged ||
    typeChanged ||
    colorChanged ||
    labelChanged
  ) {
    inner.innerHTML =
      markerHtml(
        vehicle,
        status,
        isSelected,
        rotation,
      );
  }
}
